import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { DriverProvider, useDriver } from './context/DriverContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { handlePushDeepLink } from './services/notifications';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ActiveTrip from './pages/ActiveTrip';
import RequiredUpdateGate from './components/RequiredUpdateGate';

// Cold start only ever needs Login/Dashboard/ActiveTrip (the last one for a
// resumed trip after a force-close) — these four are reachable only by an
// explicit tap once the app is already open, so load their code on demand
// instead of parsing it on every cold start.
const Profile = React.lazy(() => import('./pages/Profile'));
const Earnings = React.lazy(() => import('./pages/Earnings'));
const LegalDocumentScreen = React.lazy(() => import('./pages/LegalDocumentScreen'));
const Documents = React.lazy(() => import('./pages/Documents'));

const StartupShell = ({ canRetry = false, onRetry }) => (
  <div className="driver-bootstrap-shell" role="status" aria-live="polite">
    <div className="driver-bootstrap-brand">
      <span aria-hidden="true">🛺</span>
      <div><strong>MyTownly Anna</strong><small>Preparing your dashboard</small></div>
    </div>
    <div className="driver-bootstrap-card">
      <span className="driver-bootstrap-spinner" aria-hidden="true" />
      <strong>{canRetry ? 'Connection is taking longer than usual' : 'Restoring your secure session'}</strong>
      <small>{canRetry ? 'Your account is safe. Check your internet and retry.' : 'Your dashboard will be ready in a moment.'}</small>
      {canRetry && (
        <button type="button" className="btn btn-green" onClick={onRetry}>Retry</button>
      )}
    </div>
  </div>
);

const AppRoutes = () => {
  const {
    user, driver, authLoading, profileStatus, needsProfile, refreshDriverProfile,
    releaseNotice, activeRide, rideOfferOpen,
  } = useDriver();
  const location = useLocation();
  const navigate = useNavigate();
  const onDashboard = location.pathname === '/';
  const onPrimaryTab = onDashboard
    || location.pathname === '/earnings'
    || location.pathname === '/profile';
  const dashboardVisited = React.useRef(false);
  if (onDashboard) dashboardVisited.current = true;

  useEffect(() => handlePushDeepLink(navigate), [navigate]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'mt_push_navigate') {
        window.dispatchEvent(new CustomEvent('mt_push_navigate', { detail: event.data.data || {} }));
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  // Handle Android hardware back button & gestures
  useEffect(() => {
    const onHardwareBack = (e) => {
      const modalCloseBtn = document.querySelector('.modal-close, .chat-close, .cancel-btn, .dialog-close');
      if (modalCloseBtn) {
        modalCloseBtn.click();
        e?.preventDefault?.();
        return;
      }
      if (location.pathname !== '/' && location.pathname !== '/login') {
        e?.preventDefault?.();
        navigate(-1);
      }
    };

    window.__handleHardwareBack = () => {
      const modalCloseBtn = document.querySelector('.modal-close, .chat-close, .cancel-btn, .dialog-close');
      if (modalCloseBtn) {
        modalCloseBtn.click();
        return true;
      }
      if (location.pathname !== '/' && location.pathname !== '/login') {
        navigate(-1);
        return true;
      }
      return false;
    };

    window.addEventListener('mt_hardware_back', onHardwareBack);
    return () => {
      window.removeEventListener('mt_hardware_back', onHardwareBack);
      window.__handleHardwareBack = null;
    };
  }, [location.pathname, navigate]);

  // A verified local profile may paint the dashboard immediately, but
  // DriverContext keeps protected actions locked until the server confirms it.
  const hasCompleteProfile = Boolean(
    driver?.isApproved === true
    || driver?.name?.trim()
    || driver?.vehicleNumber?.trim()
    || driver?.profileComplete === true
  );
  const profileFailed = profileStatus === 'profile-error';
  const retryProfile = () => {
    if (user?.uid) refreshDriverProfile(user.uid).catch(() => {});
  };
  // Keep the Dashboard (and its Google Map instance) alive while switching
  // among the three primary tabs. This is presentation-only; route guards and
  // DriverContext still own authentication and protected actions.
  const keepDashboardMounted = Boolean(hasCompleteProfile && onPrimaryTab && dashboardVisited.current);

  const gate = (page) => {
    if (authLoading) {
      if (hasCompleteProfile && location.pathname === '/') return page;
      return <StartupShell />;
    }
    if (!user) return <Navigate to="/login" replace />;
    if (needsProfile) return <Navigate to="/login" replace />;
    if (profileStatus !== 'ready') {
      if (hasCompleteProfile && location.pathname === '/') return page;
      return <StartupShell canRetry={profileFailed} onRetry={retryProfile} />;
    }
    return page;
  };

  return (
    <>
      {keepDashboardMounted && (
        <section
          className={`persistent-driver-dashboard${onDashboard ? ' is-active' : ' is-inactive'}`}
          aria-hidden={!onDashboard}
        >
          <Dashboard isTabActive={onDashboard} />
        </section>
      )}
      <Suspense fallback={<StartupShell />}>
      <Routes>
      <Route
        path="/login"
        element={
          authLoading
            ? <StartupShell />
            : !user
            ? <Login />
            : needsProfile
                ? <Login startAt="profile" />
                : profileStatus === 'ready' || hasCompleteProfile
                  ? <Navigate to="/" replace />
                  : <StartupShell canRetry={profileFailed} onRetry={retryProfile} />
        }
      />
      <Route path="/legal/:type" element={<LegalDocumentScreen />} />
      <Route path="/privacy" element={<LegalDocumentScreen type="privacy" />} />
      <Route path="/terms" element={<LegalDocumentScreen type="terms" />} />
      <Route path="/safety" element={<LegalDocumentScreen type="safety" />} />
      <Route path="/" element={keepDashboardMounted ? null : gate(<Dashboard />)} />
      <Route path="/trip/:rideId" element={gate(<ActiveTrip />)} />
      <Route path="/earnings" element={gate(<Earnings />)} />
      <Route path="/profile" element={gate(<Profile />)} />
      <Route path="/documents" element={gate(<Documents />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      {user && (
        <RequiredUpdateGate
          notice={releaseNotice}
          activeRide={activeRide}
          rideOfferOpen={rideOfferOpen}
        />
      )}
    </>
  );
};

export default function App() {
  return (
    <LanguageProvider>
      <DriverProvider>
        <BrowserRouter>
          <main id="main-content">
            <AppRoutes />
          </main>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '14px',
                background: '#1a1d24',
                color: '#e5e7eb',
                border: '1px solid #2a2d35',
              },
            }}
          />
        </BrowserRouter>
      </DriverProvider>
    </LanguageProvider>
  );
}
